/**
 * External Imports
 */
import { FC, Suspense, useCallback, useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

/**
 * Nexus Imports
 */
import { NeuApp } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { mainNavRouteObjects } from './Route';
import Loading from './components/shared/Loading';
import ErrorPage401 from './pages/errorPage/401';
import ErrorPage403 from './pages/errorPage/403';
import ErrorPage404 from './pages/errorPage/404';
import ProtectedRoute from './pages/protectedRoute/ProtectedRoute';
import Login from './pages/login/Login';
import NoAccessLocation from './pages/errorPage/NoAccessLocation';
import { clearUser, setFeatures, setSessionId } from './redux/actions';
import { useLazyGetFacilitiesQuery } from './redux/apis/Config.api';
import {
  useLazyGetAuthorizedUserQuery,
  usePostUpdateCurrentFacilityMutation
} from './redux/apis/Users.api';

import api from './services/API';
import ModalPortal from './services/ModalPortal';
import { Toast } from './services/Toast';

import { handleAuthToken } from './services/AUTH/utils';

/**
 * Global Type Definition Imports
 */
import { Facility, RouteObj } from './config/interfaces';

/**
 * Style Imports
 */
import './styles/App.scss';
import Version from '../package.json';
import { useUserRolesRef } from './services/UserRoles';

const App: FC<{}> = () => {
  const {
    isAuthenticated,
    user,
    removeUser,
    signinRedirect,
    signinSilent,
    signoutSilent
  } = useAuth();

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { isIlobRounder, listOfRoles } = useUserRolesRef();

  const { setTrackingSessionId, setTrackingUserId, startAnalytics } =
    useAnalyticsApi();

  const [
    getAuthorizedUser,
    {
      data: authorizedUser = {}
      // isError: isGetAuthorizedUserError,
      // isFetching: isGetAuthorizedUserFetching,
      // isLoading: isGetAuthorizedUserLoading
    }
  ] = useLazyGetAuthorizedUserQuery({
    pollingInterval: 30 * 60 * 1000, // 30 minutes
    refetchOnFocus: true,
    refetchOnReconnect: true
  });
  const [
    getFacilities,
    {
      data: facilitiesData
      // isError: isGetFacilitiesError
      // isFetching: isGetFacilitiesFetching,
      // isLoading: isGetFacilitiesLoading
    }
  ] = useLazyGetFacilitiesQuery({
    pollingInterval: 30 * 60 * 1000, // 30 minutes
    refetchOnReconnect: true,
    refetchOnFocus: true
  });
  const [
    updateCurrentFacility
    // {
    //   isError: isUpdateCurrentFacilityError,
    //   isLoading: isUpdateCurrentFacilityLoading,
    //   isSuccess: isUpdateCurrentFacilitySuccess
    // }
  ] = usePostUpdateCurrentFacilityMutation();

  const returnRoute = useCallback((route: RouteObj, isAllowed: boolean) => {
    const component = isAllowed ? route.page : <ErrorPage401 />;
    return (
      <Route
        key={route.path}
        path={route.path}
        element={<ProtectedRoute component={component} />}
      />
    );
  }, []);

  const createRoutes = useCallback(() => {
    const approvedRoutes: JSX.Element[] = [];
    let isAllowed = false;
    mainNavRouteObjects.forEach(routeItem => {
      if (
        routeItem.allowedRoles.some(role => (listOfRoles ?? []).includes(role))
      ) {
        isAllowed = true;
        approvedRoutes.push(returnRoute(routeItem, isAllowed));
      }
    });
    return approvedRoutes;
  }, [listOfRoles, mainNavRouteObjects]);

  const clearUserFromMemory = useCallback(() => {
    window.localStorage.removeItem('authToken');
    window.localStorage.removeItem('34');
    sessionStorage.removeItem('sessionId');
    dispatch(setSessionId(''));
    dispatch(clearUser());
  }, []);

  useEffect(() => {
    const ORBIT = 'Orbit';

    startAnalytics(
      ORBIT,
      Version.version,
      process.env.REACT_APP_ANALYTICS_API_KEY,
      process.env.REACT_APP_ENV
    );
  }, []);

  useEffect(() => {
    api.setRequestInterceptors(
      navigate,
      signinSilent,
      removeUser,
      signoutSilent,
      clearUserFromMemory
    );
    api.setResponseInterceptors(
      navigate,
      signinSilent,
      removeUser,
      signoutSilent,
      clearUserFromMemory
    );
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (isAuthenticated) {
      console.log('Authorization request complete.');
      handleAuthToken();
      console.log('Getting user from back-end...');
      const sessionId = uuidv4();
      if (!sessionStorage.getItem('sessionId')) {
        sessionStorage.setItem('sessionId', sessionId);
        dispatch(setSessionId(sessionId));
        setTrackingSessionId(sessionId);
        setTrackingUserId(localStorage.getItem('34') || '');
      }
      const storedUser34 = localStorage.getItem('34');
      if (storedUser34) getAuthorizedUser(storedUser34);
    } else if (
      !code &&
      !user &&
      !sessionStorage.getItem(
        `oidc.user:${process.env.REACT_APP_SSO_URL}:${process.env.REACT_APP_SSO_OAUTH_CLIENT_ID}`
      ) &&
      process.env.REACT_APP_ENV === 'production' &&
      window.location.pathname !== '/401' &&
      window.location.pathname !== '/403' &&
      window.location.pathname !== '/404'
    ) {
      console.log('Authorizing user...');
      signinRedirect();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (Object.keys(authorizedUser).length > 0) {
      getFacilities(authorizedUser, true);
    }
  }, [authorizedUser]);

  useEffect(() => {
    const userAuth = authorizedUser?.userAuthorizations;
    const initialAccessLevel =
      userAuth && userAuth[0]?.accessLevels[0]?.accessLevel;
    const initialAccessLevelIds =
      userAuth &&
      userAuth[0]?.accessLevels.map(
        (accessLevel: { accessLevel: string; accessLevelId: string }) =>
          accessLevel.accessLevelId
      );

    if (authorizedUser) {
      if (isIlobRounder) {
        return;
      }
      if (
        userAuth?.length === 0 ||
        (userAuth &&
          userAuth?.length > 0 &&
          userAuth[0]?.accessLevels.length === 0)
      ) {
        navigate('/400');
        return;
      }
      if (
        initialAccessLevel === 'facility' &&
        initialAccessLevelIds.includes(authorizedUser?.facilityId)
      ) {
        updateCurrentFacility({
          userId: authorizedUser.hcaid,
          facilityId: userAuth[0]?.accessLevels[0]?.accessLevelId
        });
      } else if (
        initialAccessLevel === 'division' &&
        facilitiesData?.facilities &&
        facilitiesData.facilities.length > 0 &&
        !initialAccessLevelIds.includes(
          facilitiesData.facilities.filter(
            (facility: Facility) =>
              facility.facilityId === authorizedUser?.facilityId
          )[0]?.division
        )
      ) {
        updateCurrentFacility({
          userId: authorizedUser.hcaid,
          facilityId:
            facilitiesData.facilities.find(
              (facility: Facility) =>
                facility.division === initialAccessLevelIds[0]
            )?.facilityId || authorizedUser?.facilityId
        });
      }

      // Turning on any facility features
      if (
        authorizedUser.facilityId &&
        facilitiesData?.facilities &&
        facilitiesData.facilities.length > 0
      ) {
        dispatch(
          setFeatures({
            isCSCEnabled:
              facilitiesData.facilities.find(
                (facility: Facility) =>
                  facility.facilityId === authorizedUser?.facilityId
              )?.isCsrnEnabled ?? false,
            isCNEdEnabled:
              facilitiesData.facilities.find(
                (facility: Facility) =>
                  facility.facilityId === authorizedUser?.facilityId
              )?.isCnedEnabled ?? false,
            isNLRValidationEnabled:
              facilitiesData.facilities.find(
                (facility: Facility) =>
                  // TODO: change to isNLR... when NLR and STAR splits
                  facility.facilityId === authorizedUser?.facilityId
              )?.isValidationEnabled ?? false,
            isAIDETValidationEnabled:
              facilitiesData.facilities.find(
                (facility: Facility) =>
                  facility.facilityId === authorizedUser?.facilityId
              )?.isAIDETValidationEnabled ?? false,
            isBSSRValidationEnabled:
              facilitiesData.facilities.find(
                (facility: Facility) =>
                  facility.facilityId === authorizedUser?.facilityId
              )?.isBSSRValidationEnabled ?? false,
            isSafetyValidationEnabled:
              facilitiesData.facilities.find(
                (facility: Facility) =>
                  facility.facilityId === authorizedUser?.facilityId
              )?.isSafetyValidationEnabled ?? false
          })
        );
      }
    }
  }, [authorizedUser, facilitiesData]);

  return (
    <NeuApp color="white">
      <Suspense fallback={<Loading />}>
        <Routes>
          {createRoutes()}
          <Route path="/login" element={<Login />} />
          <Route path="/401" element={<ErrorPage401 />} />
          <Route path="/403" element={<ErrorPage403 />} />
          <Route path="/404" element={<ErrorPage404 />} />
          <Route path="/400" element={<NoAccessLocation />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </Suspense>
      <ModalPortal />
      <Toast />
    </NeuApp>
  );
};

export default App;
